// Selection tool (V): click to select (Shift adds/removes), drag to move (Shift: horizontal/vertical/45°,
// Alt: move a copy), drag a box handle to scale (Shift: keep proportions, Alt: from the centre), drag just
// outside a corner to rotate (Shift: 15° steps), drag on empty canvas to select by marquee.
import type { Tool, ToolHost, ToolPointer } from './tool';
import type { Mat2D, Rect } from '../model/types';
import { useDesign } from '../state';
import { useDesignUi } from '../ui/designUi';
import { hitItem, itemsInRect } from '../geometry/hitTest';
import { handlePoints, opposite, selectionBounds } from './selection';
import { about, mul, rotate, scale, translate } from '../geometry/mat';
import { duplicateNodes, moveGuide, removeGuide, transformNodes } from '../commands/commands';
import { findNode } from '../model/tree';
import { toScreen } from '../render/viewMath';
import { renderer } from '../render/instance';
import { drawSnapGuides, snapContext, type SnapCtx } from './snapContext';
import type { Guide } from '../geometry/snapping';

const HANDLE = 4; // half size in screen px
const ROTATE_ZONE = 16; // screen px outside a corner

type Drag =
  | { kind: 'none' }
  | { kind: 'marquee'; x0: number; y0: number; x1: number; y1: number; add: boolean }
  | { kind: 'move'; x0: number; y0: number; start: Map<string, Mat2D>; copied: boolean; box: Rect; snap: SnapCtx; moved: boolean; hitId: string; wasSelected: boolean }
  | { kind: 'scale'; handle: number; box: Rect; start: Map<string, Mat2D>; snap: SnapCtx }
  | { kind: 'rotate'; cx: number; cy: number; a0: number; start: Map<string, Mat2D> }
  | { kind: 'guide'; id: string; axis: 'x' | 'y' };

let gesture = 0;

function startTransforms(ids: string[]): Map<string, Mat2D> {
  const doc = useDesign.getState().doc!;
  const m = new Map<string, Mat2D>();
  for (const id of ids) {
    const f = findNode(doc, id);
    if (f) m.set(id, [...f.node.transform] as Mat2D);
  }
  return m;
}

function applyDelta(start: Map<string, Mat2D>, delta: Mat2D, key: string) {
  const next = new Map<string, Mat2D>();
  for (const [id, m] of start) next.set(id, mul(delta, m));
  useDesign.getState().run(transformNodes(next, key));
}

export class SelectTool implements Tool {
  private drag: Drag = { kind: 'none' };
  private guides: Guide[] = [];
  private key = '';

  /** Which box handle (or rotate zone) is under the screen point, if any. */
  private handleAt(p: ToolPointer, h: ToolHost): { handle: number; rotate: boolean } | null {
    const sel = useDesignUi.getState().selection;
    const box = selectionBounds(h.list, sel);
    if (!box) return null;
    const v = h.view();
    const pts = handlePoints(box).map(([x, y]) => toScreen(v, x, y));
    for (let i = 0; i < 8; i++) {
      const [x, y] = pts[i]!;
      if (Math.abs(p.sx - x) <= HANDLE + 2 && Math.abs(p.sy - y) <= HANDLE + 2)
        return { handle: i, rotate: false };
    }
    for (const i of [0, 2, 4, 6]) {
      const [x, y] = pts[i]!;
      const d = Math.hypot(p.sx - x, p.sy - y);
      if (d > HANDLE + 2 && d <= ROTATE_ZONE + HANDLE) return { handle: i, rotate: true };
    }
    return null;
  }

  down(p: ToolPointer, h: ToolHost) {
    this.key = `select-${++gesture}`;
    const ui = useDesignUi.getState();
    const hd = this.handleAt(p, h);
    if (hd) {
      const box = selectionBounds(h.list, ui.selection)!;
      renderer.setFloating(ui.selection);
      if (hd.rotate) {
        const cx = box.x + box.w / 2,
          cy = box.y + box.h / 2;
        this.drag = {
          kind: 'rotate',
          cx,
          cy,
          a0: Math.atan2(p.wy - cy, p.wx - cx),
          start: startTransforms(ui.selection),
        };
      } else this.drag = { kind: 'scale', handle: hd.handle, box, start: startTransforms(ui.selection), snap: snapContext(ui.selection) };
      return;
    }
    const it = hitItem(h.list, p.wx, p.wy, 3 * p.px);
    if (it) {
      const wasSelected = ui.selection.includes(it.topId);
      let sel = ui.selection;
      if (p.shift) sel = sel.includes(it.topId) ? sel.filter((s) => s !== it.topId) : [...sel, it.topId];
      else if (!sel.includes(it.topId)) sel = [it.topId];
      ui.set({ selection: sel });
      if (!sel.includes(it.topId)) {
        this.drag = { kind: 'none' };
        return;
      }
      this.drag = {
        kind: 'move',
        x0: p.wx,
        y0: p.wy,
        start: startTransforms(sel),
        copied: false,
        box: selectionBounds(h.list, sel)!,
        snap: snapContext(sel),
        moved: false,
        hitId: it.topId,
        wasSelected,
      };
      renderer.setFloating(sel);
      return;
    }
    // A guide under the pointer (4 px) is dragged; dropped on a ruler, it goes away.
    const doc = useDesign.getState().doc;
    const gd = useDesignUi.getState().showGuides
      ? doc?.guides.find((g) => Math.abs((g.axis === 'x' ? p.wx : p.wy) - g.pos) <= 4 * p.px)
      : undefined;
    if (gd) {
      this.drag = { kind: 'guide', id: gd.id, axis: gd.axis };
      return;
    }
    if (!p.shift) ui.set({ selection: [] });
    this.drag = { kind: 'marquee', x0: p.wx, y0: p.wy, x1: p.wx, y1: p.wy, add: p.shift };
  }

  move(p: ToolPointer, h: ToolHost, dragging: boolean) {
    const d = this.drag;
    if (!dragging || d.kind === 'none') {
      const hd = this.handleAt(p, h);
      h.setCursor(
        hd
          ? hd.rotate
            ? 'alias'
            : ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'][hd.handle % 4]!
          : 'default',
      );
      return;
    }
    if (d.kind === 'marquee') {
      d.x1 = p.wx;
      d.y1 = p.wy;
      h.redrawOverlay();
      return;
    }
    if (d.kind === 'guide') {
      useDesign.getState().run(moveGuide(d.id, d.axis === 'x' ? p.wx : p.wy, this.key));
      return;
    }
    if (d.kind === 'move') {
      let dx = p.wx - d.x0,
        dy = p.wy - d.y0;
      if (p.shift) {
        // Constrain to 0/45/90°.
        const a = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
        const len = Math.hypot(dx, dy) * Math.cos(Math.atan2(dy, dx) - a);
        dx = len * Math.cos(a);
        dy = len * Math.sin(a);
      }
      d.moved = true;
      // Smart guides / snapping on the selection's box (Shift's 45° constraint wins over snapping).
      if (!p.shift) {
        const s = d.snap.box(d.box, dx, dy);
        dx = s.dx;
        dy = s.dy;
        this.guides = s.guides;
      } else this.guides = [];
      if (p.alt && !d.copied) {
        // Alt-drag: leave copies behind at the start position, keep dragging the originals.
        d.copied = true;
        useDesign.getState().run({ ...duplicateNodes([...d.start.keys()]), merge: this.key });
      }
      applyDelta(d.start, translate(dx, dy), this.key);
      return;
    }
    if (d.kind === 'scale') {
      const pts = handlePoints(d.box);
      // The dragged handle snaps like a point.
      const sp = d.snap.point(p.wx, p.wy);
      this.guides = sp.guides;
      p = { ...p, wx: sp.x, wy: sp.y };
      const [ax, ay] = p.alt ? [d.box.x + d.box.w / 2, d.box.y + d.box.h / 2] : pts[opposite(d.handle)]!;
      const [hx, hy] = pts[d.handle]!;
      const horiz = d.handle !== 1 && d.handle !== 5,
        vert = d.handle !== 3 && d.handle !== 7;
      let sx = horiz && hx !== ax ? (p.wx - ax) / (hx - ax) : 1;
      let sy = vert && hy !== ay ? (p.wy - ay) / (hy - ay) : 1;
      if (p.shift) {
        const s = horiz && vert ? Math.max(Math.abs(sx), Math.abs(sy)) : horiz ? Math.abs(sx) : Math.abs(sy);
        sx = Math.sign(sx || 1) * s;
        sy = Math.sign(sy || 1) * s;
      }
      if (Math.abs(sx) < 1e-4) sx = 1e-4;
      if (Math.abs(sy) < 1e-4) sy = 1e-4;
      applyDelta(d.start, about(scale(sx, sy), ax, ay), this.key);
      return;
    }
    if (d.kind === 'rotate') {
      let a = Math.atan2(p.wy - d.cy, p.wx - d.cx) - d.a0;
      if (p.shift) a = Math.round(a / (Math.PI / 12)) * (Math.PI / 12);
      applyDelta(d.start, about(rotate(a), d.cx, d.cy), this.key);
    }
  }

  up(p: ToolPointer, h: ToolHost) {
    const d = this.drag;
    this.guides = [];
    if (d.kind === 'guide' && (d.axis === 'x' ? p.sx < 0 : p.sy < 0)) useDesign.getState().run(removeGuide(d.id, this.key));
    // A click (no drag) on an object that was already in a multiple selection makes it the key object.
    if (d.kind === 'move' && !d.moved && d.wasSelected && useDesignUi.getState().selection.length > 1)
      useDesignUi.getState().set({ keyObject: d.hitId });
    else if (d.kind === 'move' && !d.moved && !d.wasSelected) useDesignUi.getState().set({ keyObject: null });
    if (d.kind === 'marquee') {
      const r = {
        x: Math.min(d.x0, d.x1),
        y: Math.min(d.y0, d.y1),
        w: Math.abs(d.x1 - d.x0),
        h: Math.abs(d.y1 - d.y0),
      };
      if (r.w > 0 || r.h > 0) {
        const ids = [...new Set(itemsInRect(h.list, r).map((it) => it.topId))];
        const ui = useDesignUi.getState();
        ui.set({ selection: d.add ? [...new Set([...ui.selection, ...ids])] : ids });
      }
    }
    this.drag = { kind: 'none' };
    renderer.setFloating([]);
    useDesign.getState().endGesture();
    h.redrawOverlay();
  }

  overlay(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>, h: ToolHost) {
    const d = this.drag;
    drawSnapGuides(g, v, this.guides);
    if (d.kind === 'marquee') {
      const [x0, y0] = toScreen(v, d.x0, d.y0),
        [x1, y1] = toScreen(v, d.x1, d.y1);
      g.strokeStyle = '#4f8cff';
      g.setLineDash([3, 3]);
      g.strokeRect(Math.min(x0, x1) + 0.5, Math.min(y0, y1) + 0.5, Math.abs(x1 - x0), Math.abs(y1 - y0));
      g.setLineDash([]);
    }
    drawSelectionBox(g, v, h);
  }
}

/** The selection's outlines and bounding box with its 8 handles (shared by the selection-type tools). */
export function drawSelectionBox(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>, h: ToolHost) {
  const sel = useDesignUi.getState().selection;
  if (!sel.length) return;
  const set = new Set(sel);
  g.strokeStyle = '#4f8cff';
  g.lineWidth = 1;
  // Outlines of the selected items (only for modest selections: a 10,000-item outline is noise).
  let n = 0;
  for (const it of h.list.items) {
    if (!set.has(it.topId) || it.hidden || !it.path) continue;
    if (++n > 400) break;
    g.save();
    g.transform(v.zoom, 0, 0, v.zoom, v.panX, v.panY);
    g.transform(...it.world);
    const s = Math.sqrt(Math.abs(it.world[0] * it.world[3] - it.world[1] * it.world[2])) || 1;
    g.lineWidth = 1 / (v.zoom * s);
    g.stroke(it.path);
    g.restore();
  }
  const box = selectionBounds(h.list, sel);
  if (!box) return;
  const [x0, y0] = toScreen(v, box.x, box.y),
    [x1, y1] = toScreen(v, box.x + box.w, box.y + box.h);
  g.strokeRect(Math.round(x0) + 0.5, Math.round(y0) + 0.5, Math.round(x1 - x0), Math.round(y1 - y0));
  // The key object for Align: a thick outline around its box.
  const key = useDesignUi.getState().keyObject;
  if (key && sel.includes(key)) {
    const kb = selectionBounds(h.list, [key]);
    if (kb) {
      const [k0x, k0y] = toScreen(v, kb.x, kb.y),
        [k1x, k1y] = toScreen(v, kb.x + kb.w, kb.y + kb.h);
      g.lineWidth = 3;
      g.strokeRect(k0x, k0y, k1x - k0x, k1y - k0y);
      g.lineWidth = 1;
    }
  }
  g.fillStyle = '#fff';
  for (const [x, y] of handlePoints(box)) {
    const [sx, sy] = toScreen(v, x, y);
    g.fillRect(Math.round(sx) - HANDLE + 0.5, Math.round(sy) - HANDLE + 0.5, 2 * HANDLE, 2 * HANDLE);
    g.strokeRect(Math.round(sx) - HANDLE + 0.5, Math.round(sy) - HANDLE + 0.5, 2 * HANDLE, 2 * HANDLE);
  }
}
